import QuickModal from '../components/quick/QuickModal';
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch, parseJsonSafe, API_URL } from '../utils/api';
import LeadWhatsappConfig, { completeRuleVariables } from '../components/LeadWhatsappConfig';

const statusOptions = [
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'contactado', label: 'Contactado' },
  { value: 'sin_respuesta', label: 'Sin respuesta' },
  { value: 'presupuesto_enviado', label: 'Presupuesto enviado' },
  { value: 'presupuesto_rechazado', label: 'Presupuesto rechazado' },
  { value: 'ganado', label: 'Ganado' },
  { value: 'perdido', label: 'Perdido' }
];

const statusBadge = (status) => {
  switch (status) {
    case 'contactado': return 'badge bg-primary';
    case 'sin_respuesta': return 'badge bg-secondary';
    case 'presupuesto_enviado': return 'badge bg-info text-dark';
    case 'presupuesto_rechazado': return 'badge bg-warning text-dark';
    case 'ganado': return 'badge bg-success';
    case 'perdido': return 'badge bg-danger';
    default: return 'badge bg-light text-dark';
  }
};

// Resultado del WhatsApp automático de cada lead
const WA_STATUS = {
  programado: { label: 'Programado', cls: 'badge bg-warning text-dark' },
  enviado: { label: 'Enviado', cls: 'badge bg-primary' },
  enviando: { label: 'Enviando', cls: 'badge bg-info text-dark' },
  error: { label: 'Error', cls: 'badge bg-danger' },
  omitido: { label: 'Omitido', cls: 'badge bg-secondary' },
  sin_regla: { label: 'Sin regla', cls: 'badge bg-light text-dark border' }
};

const waTitle = (l) => [
  l.whatsapp_template ? `Plantilla: ${l.whatsapp_template}` : '',
  l.whatsapp_phone ? `Teléfono: +${l.whatsapp_phone}` : '',
  l.whatsapp_status === 'programado' && l.whatsapp_send_after ? `Programado para: ${new Date(l.whatsapp_send_after).toLocaleString()}` : '',
  l.whatsapp_sent_at ? `Enviado: ${new Date(l.whatsapp_sent_at).toLocaleString()}` : '',
  l.whatsapp_error || ''
].filter(Boolean).join('\n');

const WebhookPage = () => {
  const token = localStorage.getItem('token');

  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [configOpen, setConfigOpen] = useState(false);
  const [allowedIpsText, setAllowedIpsText] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [whatsappCountryCode, setWhatsappCountryCode] = useState('');
  // WhatsApp automático a leads nuevos
  const [leadWa, setLeadWa] = useState(null);           // { templates, templatesError, campaigns, stats }
  const [leadWaConfig, setLeadWaConfig] = useState({ enabled: false, dedupeDays: 7, rules: [] });
  const [leadWaRefreshing, setLeadWaRefreshing] = useState(false);

  const [messageLead, setMessageLead] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    campaign: '',
    service: '',
    utm_medium: '',
    utm_source: '',
    status: ''
  });

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const buildWebhookUrl = () => {
    const base = API_URL || '';
    const path = '/api/google-ads/webhook';
    if (base.startsWith('http')) {
      return `${base.replace(/\/$/, '')}${path}`;
    }
    return `${window.location.origin}${base}${path}`;
  };

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const res = await apiFetch(`/api/google-ads/leads?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando leads');
      setLeads(data.items || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLeadWa = async (refresh = false) => {
    try {
      const res = await apiFetch(`/api/google-ads/lead-whatsapp${refresh ? '?refresh=1' : ''}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar el WhatsApp automático');
      setLeadWa({ templates: data.templates || [], templatesError: data.templatesError || '', campaigns: data.campaigns || [], stats: data.stats || [], defaultScript: data.defaultScript || '' });
      if (!refresh) setLeadWaConfig(data.config || { enabled: false, dedupeDays: 7, rules: [] });
    } catch (err) {
      setLeadWa((prev) => ({ ...(prev || {}), templatesError: err.message }));
    }
    return null;
  };

  const loadConfig = async () => {
    loadLeadWa();
    try {
      const res = await apiFetch('/api/google-ads/config', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        const allowed = Array.isArray(data.allowedIps) ? data.allowedIps : [];
        setAllowedIpsText(allowed.join('\n'));
        setWhatsappCountryCode(data.whatsappCountryCode || '');
      }
    } catch (err) {
      console.error('Error cargando config webhook', err);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const allowedIps = allowedIpsText
        .split(/[\n,]/)
        .map((v) => v.trim())
        .filter(Boolean);
      const payload = { allowedIps, whatsappCountryCode };
      const res = await apiFetch('/api/google-ads/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      const waRes = await apiFetch('/api/google-ads/lead-whatsapp', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(completeRuleVariables(leadWaConfig, leadWa?.templates || []))
      });
      const waData = await parseJsonSafe(waRes);
      if (waRes.status === 401) return handleUnauthorized();
      if (!waRes.ok) throw new Error(waData.message || 'No se pudo guardar el WhatsApp automático');
      alert(waData.warning ? `Configuración guardada.\n\nAviso: ${waData.warning}` : 'Configuración guardada');
      await loadConfig();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await apiFetch(`/api/google-ads/leads/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters]);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <h2 className="mb-0">Webhook</h2>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button className="btn btn-outline-secondary btn-sm" onClick={async () => { await loadConfig(); setConfigOpen(true); }}>
            Configuración
          </button>
          <button
            className="btn btn-outline-dark btn-sm"
            title="Copiar JWT token para API externa"
            onClick={() => {
              const t = localStorage.getItem('token');
              if (t) {
                navigator.clipboard.writeText(t).then(() => alert('JWT token copiado al portapapeles'));
              } else {
                alert('No hay token disponible. Inicia sesión primero.');
              }
            }}
          >
            Copiar JWT
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Leads webhook</h5>
            <div className="text-muted small">Total: {total}</div>
          </div>
          <div className="row g-2 mb-3">
            <div className="col-md-3 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="Nombre"
                value={filters.name}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, name: e.target.value })); }}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="Email"
                value={filters.email}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, email: e.target.value })); }}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="Teléfono"
                value={filters.phone}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, phone: e.target.value })); }}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="Campaña"
                value={filters.campaign}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, campaign: e.target.value })); }}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="Servicio"
                value={filters.service}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, service: e.target.value })); }}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="UTM medium"
                value={filters.utm_medium}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, utm_medium: e.target.value })); }}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="Origen (utm_source)"
                value={filters.utm_source}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, utm_source: e.target.value })); }}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <input
                className="form-control form-control-sm"
                placeholder="Mensaje"
                value={filters.message}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, message: e.target.value })); }}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <select
                className="form-select form-select-sm"
                value={filters.status}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, status: e.target.value })); }}
              >
                <option value="">Estado (todos)</option>
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2 col-sm-6 d-grid">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setPage(1);
                  setFilters({ name: '', email: '', phone: '', message: '', campaign: '', service: '', utm_medium: '', utm_source: '', status: '' });
                }}
              >
                Limpiar filtros
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th>WhatsApp</th>
                  <th>Origen</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Campaña</th>
                  <th>Servicio</th>
                  <th>UTM medium</th>
                  <th>Mensaje</th>
                </tr>
              </thead>
              <tbody>
                {!loading && leads.length === 0 && <tr><td colSpan="11" className="text-muted small">Sin leads.</td></tr>}
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td>{l.created_at ? new Date(l.created_at).toLocaleString() : '-'}</td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <span className={`${statusBadge(l.status)} text-uppercase`}>{l.status || 'nuevo'}</span>
                        <select
                          className="form-select form-select-sm"
                          value={l.status || 'nuevo'}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                        >
                          {statusOptions.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td>
                      {l.whatsapp_status
                        ? <span className={WA_STATUS[l.whatsapp_status]?.cls || 'badge bg-light text-dark border'} title={waTitle(l)}>{WA_STATUS[l.whatsapp_status]?.label || l.whatsapp_status}</span>
                        : <span className="text-muted">—</span>}
                      {l.whatsapp_replied_at && (
                        <div className="small text-primary mt-1" title={`Respondió el ${new Date(l.whatsapp_replied_at).toLocaleString()}`}>Respondió</div>
                      )}
                      {l.call_scheduled_at && (
                        <div className="small fw-semibold mt-1" title="Llamada acordada por WhatsApp">
                          Llamada {new Date(l.call_scheduled_at).toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </td>
                    <td>{l.utm_source || '-'}</td>
                    <td>{l.name}</td>
                    <td>{l.email}</td>
                    <td>{l.phone}</td>
                    <td>{l.campaign || '-'}</td>
                    <td>{l.service || '-'}</td>
                    <td>{l.utm_medium || '-'}</td>
                    <td>{l.message ? <button type="button" className="btn btn-link p-0 text-start" title="Leer mensaje completo" onClick={() => setMessageLead(l)}>{l.message.length > 80 ? `${l.message.slice(0, 80)}…` : l.message}</button> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>Página {page} de {totalPages}</div>
            <div className="d-flex gap-2 align-items-center">
              <select className="form-select form-select-sm" style={{ width: 110 }} value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
                {[10, 20, 50].map((n) => <option key={n} value={n}>{n} / pág</option>)}
              </select>
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {messageLead && <QuickModal title={`Mensaje de ${messageLead.name || 'lead'}`} onClose={() => setMessageLead(null)}><p className="text-muted">{messageLead.email} · {messageLead.phone}</p><div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{messageLead.message}</div></QuickModal>}
      {configOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configuración Webhook</h5>
                <button className="btn-close" onClick={() => setConfigOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small">IPs permitidas (una por línea)</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={allowedIpsText}
                    onChange={(e) => setAllowedIpsText(e.target.value)}
                    placeholder="1.2.3.4\n5.6.7.0/24"
                  />
                  <div className="text-muted small mt-1">Si la lista está vacía, el webhook rechazará todas las peticiones.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label small">Prefijo país por defecto (WhatsApp)</label>
                  <input
                    className="form-control"
                    placeholder="+34"
                    value={whatsappCountryCode}
                    onChange={(e) => setWhatsappCountryCode(e.target.value)}
                  />
                  <div className="text-muted small mt-1">Se usa para normalizar el teléfono al enviar WhatsApp (facturas, presupuestos y clientes).</div>
                </div>
                <LeadWhatsappConfig
                  data={leadWa}
                  config={leadWaConfig}
                  onChange={setLeadWaConfig}
                  refreshing={leadWaRefreshing}
                  onRefreshTemplates={async () => { setLeadWaRefreshing(true); await loadLeadWa(true); setLeadWaRefreshing(false); }}
                  token={token}
                  onUnauthorized={handleUnauthorized}
                />
                <div className="mb-3">
                  <div className="fw-semibold">Webhook</div>
                  <div className="text-muted small mb-1">Envía un POST con JSON al siguiente endpoint:</div>
                  <div className="border rounded p-2 bg-light small" style={{ wordBreak: 'break-all' }}>{buildWebhookUrl()}</div>
                </div>
                <div>
                  <div className="fw-semibold mb-1">Formato esperado</div>
                  <pre className="border rounded p-2 bg-light small mb-0">{`{
  "name": "Juan Perez",
  "email": "juan@dominio.com",
  "phone": "+34 600 000 000",
  "message": "Quiero info (opcional)",
  "service": "web_clinicas",
  "utm_source": "google",
  "utm_medium": "cpc",
  "utm_campaign": "enero_2026",
  "gclid": "EAIaIQob..."
}`}</pre>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setConfigOpen(false)}>Cerrar</button>
                <button className="btn btn-primary" onClick={saveConfig} disabled={savingConfig}>
                  {savingConfig ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookPage;
